import { useEffect, useMemo, useState } from "react";
import {
  listImplementationTemplates, updateImplementationTemplate, cloneImplementationTemplate,
} from "../lib/adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Pencil, Check, X, Plus, Trash2, ChevronLeft, Settings2, Layers, Clock, Copy,
  ChevronUp, ChevronDown, FolderPlus, GripVertical,
} from "lucide-react";

// Default weekly hours applied to every existing phase when an admin adds a
// new resource. Keeps the effort matrix filled out instead of leaving silent
// zeros that look like missing data.
const DEFAULT_NEW_RESOURCE_HOURS = 40;

/**
 * Minimal HTML5 drag-and-drop reordering helper.
 *
 * Returns stable handler factories + visual-state helpers. Callers provide a
 * `getKey(item, index)` function and an `onReorder(fromIdx, toIdx)` callback.
 * Mirrors the pattern used in QuestionsPage.jsx for consistency.
 */
function useDragReorder() {
  const [srcKey, setSrcKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  const bind = (key, idx, onReorder, indexOfKey) => ({
    draggable: true,
    onDragStart: (e) => {
      setSrcKey(key);
      e.dataTransfer.effectAllowed = "move";
      try { e.dataTransfer.setData("text/plain", String(key)); } catch (_) {}
    },
    onDragOver: (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (overKey !== key) setOverKey(key);
    },
    onDragLeave: () => {
      if (overKey === key) setOverKey(null);
    },
    onDrop: (e) => {
      e.preventDefault();
      const from = indexOfKey(srcKey);
      setSrcKey(null);
      setOverKey(null);
      if (from < 0 || from === idx) return;
      onReorder(from, idx);
    },
    onDragEnd: () => { setSrcKey(null); setOverKey(null); },
  });

  return {
    bind,
    isDragging: (key) => srcKey === key,
    isOver: (key) => overKey === key && srcKey !== null && srcKey !== key,
  };
}

/**
 * Admin — Implementation Templates (with one-level phase grouping).
 *
 * Canonical data shape stored on each template:
 *   phase_groups: [
 *     { name, order, warehouse_count?, phases: [
 *         { id, name, duration_weeks, order, effort: { [resource_name]: hours } }
 *       ] }
 *   ]
 *
 * The legacy flat `phases` + `effort_matrix` are kept for compatibility but
 * never written from this UI.
 */
const SOLUTION_LABELS = {
  ewm_advanced: "SAP EWM Advanced",
  ewm_basic: "SAP EWM Basic",
  lgm: "SAP LGM",
  strm: "SAP Stock Room Management",
};

// Cheap non-crypto id helper (used for new phases added client-side before save).
const newPid = () => (typeof crypto !== "undefined" && crypto.randomUUID)
  ? crypto.randomUUID()
  : `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export default function ImplementationTemplatesPage() {
  const [items, setItems] = useState([]);
  const [mode, setMode] = useState("list");
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const anyFilterActive = Object.keys(DEFAULT_FILTERS).some(
    (k) => filters[k] !== DEFAULT_FILTERS[k]);

  const filtered = useMemo(() => {
    const q = (filters.search || "").trim().toLowerCase();
    return items.filter((t) => {
      const app = t.application || (t.solution_type === "wricef_matrix" ? "SmartImprove" : "SmartEvaluate");
      if (filters.application !== "all" && app !== filters.application) return false;
      if (filters.evaluation_type !== "all" && (t.evaluation_type || "") !== filters.evaluation_type) return false;
      if (filters.transformation_path !== "all" && (t.transformation_path || "not_applicable") !== filters.transformation_path) return false;
      if (filters.template_category !== "all" && (t.template_category || "") !== filters.template_category) return false;
      if (filters.status === "active" && t.active === false) return false;
      if (filters.status === "inactive" && t.active !== false) return false;
      if (q) {
        const hay = `${t.template_name || ""} ${t.solution_label || ""} ${t.description || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, filters]);

  const load = () => listImplementationTemplates().then((all) => {
    // June 2026 — Admin surfaces strategy templates grouped by Application →
    // Evaluation Type → Category. Legacy solution-based templates (EWM
    // Advanced/Basic, LGM, StRM) remain in the DB but are hidden from the UI.
    const strategy = (all || []).filter((t) => (t.category || "solution") === "strategy");
    setItems(strategy);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (t) => {
    setEditing(JSON.parse(JSON.stringify(t)));
    setMode("edit");
  };
  const closeEdit = () => { setEditing(null); setMode("list"); };

  const onClone = async (t) => {
    try {
      const created = await cloneImplementationTemplate(t.id);
      await load();
      openEdit(created);
      setMsg(`Cloned "${t.template_name}" → "${created.template_name}". Edit and save your copy.`);
      setTimeout(() => setMsg(""), 4000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error cloning template");
    }
  };

  const onSave = async (updated) => {
    // Validate both rates >= 0 and non-empty for every resource
    const invalid = (updated.resources || []).some((r) => {
      const on = Number(r.onsite_rate);
      const off = Number(r.offshore_rate);
      return !Number.isFinite(on) || on < 0 || !Number.isFinite(off) || off < 0;
    });
    if (invalid) {
      setMsg("Every resource must have a non-negative onsite and offshore rate.");
      return;
    }
    try {
      await updateImplementationTemplate(updated.id, {
        solution_type: updated.solution_type,
        template_name: updated.template_name,
        solution_label: updated.solution_label || SOLUTION_LABELS[updated.solution_type] || "",
        phase_groups: updated.phase_groups,
        resources: (updated.resources || []).map((r) => ({
          name: r.name,
          onsite_rate: Number(r.onsite_rate) || 0,
          offshore_rate: Number(r.offshore_rate) || 0,
          hourly_rate: Number(r.onsite_rate) || 0, // legacy mirror
          order: r.order,
        })),
        active: updated.active !== false,
        // June 2026 — additive display-only metadata.
        application: updated.application || null,
        evaluation_type: updated.evaluation_type || null,
        template_category: updated.template_category || null,
        transformation_path: updated.transformation_path || null,
        indicative_duration: updated.indicative_duration || null,
        display_order: Number.isFinite(Number(updated.display_order))
          ? Number(updated.display_order) : null,
        // June 2026 — SmartImprove purpose + Admin-driven default.
        purpose: updated.purpose || null,
        is_default: !!updated.is_default,
      });
      setMsg("Saved");
      setTimeout(() => setMsg(""), 2000);
      await load();
      closeEdit();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Error saving template");
    }
  };

  if (mode === "edit" && editing) {
    return <EditTemplate template={editing} onCancel={closeEdit} onSave={onSave} />;
  }

  return (
    <div data-testid="impl-templates-page">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>Implementation Templates</h1>
        <p className="text-[11px] text-[#6B7280] mt-0.5">
          Filter the template library by <strong>Application, Evaluation Type, Transformation Path, Category</strong> and status. Use <strong>Clone</strong> on any SmartImprove template to create a new editable copy.
        </p>
      </div>

      <TemplateFilterBar items={items} filters={filters} setFilters={setFilters} />

      {msg && (
        <div data-testid="impl-save-msg" className="mb-3 px-3 py-2 bg-[#E0F2FE] border border-[#025979]/20 rounded-sm text-xs text-[#025979]">{msg}</div>
      )}

      {items.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-sm p-6 text-center text-xs text-[#9CA3AF]">Loading templates…</div>
      ) : anyFilterActive ? (
        <FilteredTemplateGrid items={filtered} onEdit={openEdit} onClone={onClone} />
      ) : (
        <GroupedTemplateList items={items} onEdit={openEdit} onClone={onClone} />
      )}
    </div>
  );
}

const DEFAULT_FILTERS = { application: "all", evaluation_type: "all", transformation_path: "all", template_category: "all", status: "all", search: "" };

function TemplateFilterBar({ items, filters, setFilters }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const evalOptions = filters.application === "SmartImprove"
    ? [["all", "Not Applicable / Effort Sizing"]]
    : [["all", "All Evaluation Types"], ["greenfield_brownfield", "Greenfield / Brownfield"], ["cloud_migration", "Cloud Migration"]];
  const cls = "px-2.5 py-1.5 text-xs border border-[#E5E7EB] rounded-sm bg-white text-[#111827] focus:outline-none focus:ring-1 focus:ring-[#025979]";
  return (
    <div data-testid="impl-filter-bar" className="mb-5 flex flex-wrap items-center gap-2">
      <select data-testid="impl-filter-application" className={cls} value={filters.application} onChange={(e) => { set("application", e.target.value); set("evaluation_type", "all"); }}>
        <option value="all">All Applications</option>
        <option value="SmartEvaluate">SmartEvaluate</option>
        <option value="SmartImprove">SmartImprove</option>
      </select>
      <select data-testid="impl-filter-evaluation-type" className={cls} value={filters.evaluation_type} onChange={(e) => set("evaluation_type", e.target.value)}>
        {evalOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <select data-testid="impl-filter-transformation-path" className={cls} value={filters.transformation_path} onChange={(e) => set("transformation_path", e.target.value)}>
        <option value="all">All Paths</option>
        <option value="technical_migration">Technical Migration</option>
        <option value="selective_migration">Selective Migration</option>
        <option value="full_transformation">Full Transformation</option>
        <option value="not_applicable">Not Applicable</option>
      </select>
      <select data-testid="impl-filter-category" className={cls} value={filters.template_category} onChange={(e) => set("template_category", e.target.value)}>
        <option value="all">All Categories</option>
        <option value="Rollout Strategy">Rollout Strategy</option>
        <option value="Migration Strategy">Migration Strategy</option>
        <option value="Transformation Strategy">Transformation Strategy</option>
        <option value="Effort Sizing">Effort Sizing</option>
      </select>
      <select data-testid="impl-filter-status" className={cls} value={filters.status} onChange={(e) => set("status", e.target.value)}>
        <option value="all">All Status</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>
      <Input data-testid="impl-filter-search" value={filters.search} onChange={(e) => set("search", e.target.value)} placeholder="Search name, strategy, description…" className="rounded-sm text-xs h-[34px] w-56" />
      {Object.keys(DEFAULT_FILTERS).some((k) => filters[k] !== DEFAULT_FILTERS[k]) && (
        <button data-testid="impl-filter-clear" onClick={() => setFilters(DEFAULT_FILTERS)} className="text-[11px] text-[#025979] underline">Clear</button>
      )}
    </div>
  );
}

function FilteredTemplateGrid({ items, onEdit, onClone }) {
  const sorted = [...items].sort((a, b) => (a.display_order ?? 99) - (b.display_order ?? 99));
  if (sorted.length === 0) {
    return <div data-testid="impl-filtered-empty" className="bg-white border border-[#E5E7EB] rounded-sm p-6 text-center text-xs text-[#9CA3AF]">No templates match the current filters.</div>;
  }
  return (
    <div data-testid="impl-filtered-grid" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {sorted.map((t) => <TemplateCard key={t.id} t={t} onEdit={onEdit} onClone={onClone} />)}
    </div>
  );
}

// Application → Evaluation Type / Category sub-section structure. Future
// applications simply appear as new top-level blocks driven by the data.
const APPLICATION_ORDER = ["SmartImprove", "SmartEvaluate", "SmartBid", "SmartEWM"];
const EVAL_SUBSECTIONS = {
  SmartEvaluate: [
    { key: "greenfield_brownfield", label: "Greenfield / Brownfield", match: (t) => t.evaluation_type === "greenfield_brownfield" },
    { key: "cloud_migration", label: "Cloud Migration", match: (t) => t.evaluation_type === "cloud_migration" },
  ],
  SmartImprove: [
    { key: "implementation_deployment", label: "Implementation / Deployment", match: (t) => t.purpose === "implementation_deployment" },
    { key: "effort_sizing", label: "Effort Sizing", match: (t) => t.purpose === "effort_sizing" },
  ],
};

const TRANSFORMATION_PATH_LABEL = {
  technical_migration: "Technical Migration",
  selective_migration: "Selective Migration",
  full_transformation: "Full Transformation",
  not_applicable: "Other",
};
const TRANSFORMATION_PATH_ORDER = ["technical_migration", "selective_migration", "full_transformation", "not_applicable"];

function GroupedTemplateList({ items, onEdit, onClone }) {
  const appOf = (t) => t.application
    || (t.solution_type === "wricef_matrix" ? "SmartImprove"
      : t.evaluation_type === "cloud_migration" ? "SmartEvaluate" : "SmartEvaluate");

  const apps = Array.from(new Set(items.map(appOf)))
    .sort((a, b) => {
      const ia = APPLICATION_ORDER.indexOf(a); const ib = APPLICATION_ORDER.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });

  const sortByOrder = (arr) => [...arr].sort(
    (a, b) => (a.display_order ?? 99) - (b.display_order ?? 99));

  // 3rd level: Cloud Migration → Transformation Path; everything else → Category.
  const buildSubGroups = (rows, isCloudMigration) => {
    const buckets = {};
    rows.forEach((t) => {
      const key = isCloudMigration ? (t.transformation_path || "not_applicable") : (t.template_category || "Templates");
      (buckets[key] = buckets[key] || []).push(t);
    });
    const keys = Object.keys(buckets);
    if (isCloudMigration) {
      keys.sort((a, b) => TRANSFORMATION_PATH_ORDER.indexOf(a) - TRANSFORMATION_PATH_ORDER.indexOf(b));
      return keys.map((k) => ({ label: TRANSFORMATION_PATH_LABEL[k] || k, key: k, rows: sortByOrder(buckets[k]) }));
    }
    return keys.map((k) => ({ label: k, key: k, rows: sortByOrder(buckets[k]) }));
  };

  return (
    <div className="space-y-8">
      {apps.map((app) => {
        const appItems = items.filter((t) => appOf(t) === app);
        let evalSections;
        if (EVAL_SUBSECTIONS[app]) {
          evalSections = EVAL_SUBSECTIONS[app]
            .map((s) => ({ label: s.label, key: s.key, isCM: s.key === "cloud_migration", rows: appItems.filter(s.match) }))
            .filter((s) => s.rows.length > 0);
        } else {
          evalSections = [{ label: null, key: "_", isCM: false, rows: appItems }];
        }
        return (
          <section key={app} data-testid={`impl-app-${app}`}>
            <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-[#025979]/20">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#025979]" style={{ fontFamily: "Chivo" }}>{app}</h2>
            </div>
            <div className="space-y-6">
              {evalSections.map((es) => (
                <div key={es.key} data-testid={`impl-section-${es.key}`}>
                  {es.label && (
                    <p className="text-[12px] font-semibold text-[#0F172A] mb-3">{es.label}</p>
                  )}
                  <div className="space-y-4 pl-1">
                    {buildSubGroups(es.rows, es.isCM).map((sg) => (
                      <div key={sg.key} data-testid={`impl-subsection-${sg.key}`}>
                        <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold mb-2">{sg.label}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                          {sg.rows.map((t) => <TemplateCard key={t.id} t={t} onEdit={onEdit} onClone={onClone} />)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function TemplateCard({ t, onEdit, onClone }) {
  const groups = t.phase_groups || [];
  const totalPhases = groups.reduce((n, g) => n + (g.phases || []).length, 0);
  // Total effort = sum of the editor's phase_groups effort (source of truth),
  // restricted to the template's actual resources so stale/phantom role keys
  // don't inflate the total. Falls back to the legacy effort_matrix.
  const roleNames = new Set((t.resources || []).map((r) => r.name || r.role).filter(Boolean));
  const totalEffort = groups.length
    ? groups.reduce((s, g) => s + (g.phases || []).reduce((ps, p) =>
        ps + Object.entries(p.effort || {}).reduce((a, [k, v]) =>
          a + (roleNames.size === 0 || roleNames.has(k) ? (Number(v) || 0) : 0), 0), 0), 0)
    : Object.values(t.effort_matrix || {}).reduce(
        (sum, row) => sum + Object.values(row || {}).reduce((a, v) => a + (Number(v) || 0), 0), 0);
  const isEffortSizing = t.purpose === "effort_sizing";
  const isDeployment = t.purpose === "implementation_deployment";
  const purposeLabel = isEffortSizing ? "Effort Sizing" : isDeployment ? "Implementation / Deployment" : (t.template_category || "Template");
  return (
    <article
      data-testid={`impl-card-${t.solution_type}`}
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-5 flex flex-col"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{purposeLabel}</p>
          <h3 className="text-base font-semibold text-[#111827] leading-tight">{t.template_name}</h3>
          <p className="text-[11px] text-[#6B7280] mt-0.5">{t.solution_label || SOLUTION_LABELS[t.solution_type] || t.solution_type}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className={`inline-flex items-center px-1.5 py-0.5 border rounded-sm text-[10px] font-medium ${
            t.active === false
              ? "bg-[#F1F5F9] text-[#6B7280] border-[#E5E7EB]"
              : "bg-[#ECFDF5] text-[#065F46] border-[#065F46]/15"
          }`}>{t.active === false ? "Inactive" : "Active"}</span>
          {t.is_default ? (
            <span data-testid={`impl-card-default-${t.solution_type}`} className="inline-flex items-center px-1.5 py-0.5 border rounded-sm text-[10px] font-semibold bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]">Default</span>
          ) : null}
        </div>
      </div>

      {isEffortSizing ? (
        <div className="grid grid-cols-2 gap-2 mb-4">
          <StatTile label="Sizing Basis" value="WRICEF" icon={<Settings2 className="w-3 h-3" />} />
          <StatTile label="Roles" value={(t.resources || []).length} icon={<Settings2 className="w-3 h-3" />} />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <StatTile label="Phases" value={totalPhases} icon={<Layers className="w-3 h-3" />} />
          <StatTile label="Resources" value={(t.resources || []).length} icon={<Settings2 className="w-3 h-3" />} />
          <StatTile label="Total Effort" value={`${Math.round(totalEffort).toLocaleString()}h`} icon={<Clock className="w-3 h-3" />} />
        </div>
      )}

      <div className="mt-auto flex justify-end gap-2">
        {onClone && (t.application === "SmartImprove") ? (
          <Button
            data-testid={`impl-clone-${t.solution_type}`}
            onClick={() => onClone(t)}
            variant="outline"
            className="rounded-sm text-xs border-[#025979]/30 text-[#025979] hover:bg-[#E0F2FE]"
          >
            <Copy className="w-3 h-3 mr-1" /> Clone
          </Button>
        ) : null}
        <Button
          data-testid={`impl-edit-${t.solution_type}`}
          onClick={() => onEdit(t)}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
        >
          <Pencil className="w-3 h-3 mr-1" /> Edit Template
        </Button>
      </div>
    </article>
  );
}

function StatTile({ label, value, icon }) {
  return (
    <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-2.5">
      <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] flex items-center gap-1">{icon}{label}</p>
      <p className="text-lg font-bold text-[#111827] mt-0.5">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit view
// ---------------------------------------------------------------------------
function EditTemplate({ template, onCancel, onSave }) {
  const [form, setForm] = useState(() => normalizeForm(template));
  const [validationError, setValidationError] = useState("");
  const isWricef = (form.solution_type || "").toLowerCase() === "wricef_matrix";

  const handleSave = () => {
    const bad = (form.resources || []).find((r) => {
      const on = Number(r.onsite_rate);
      const off = Number(r.offshore_rate);
      return !Number.isFinite(on) || on < 0 || !Number.isFinite(off) || off < 0;
    });
    if (bad) {
      setValidationError(`"${bad.name}" is missing a valid onsite or offshore rate. Both must be 0 or higher.`);
      return;
    }
    setValidationError("");
    if (isWricef) {
      // Persist only the simplified shape — strip rollout/phase data.
      const payload = {
        ...form,
        phase_groups: [],
        phases: [],
        effort_matrix: {},
        effort_matrix_simple: form.effort_matrix_simple || {},
      };
      onSave(payload);
      return;
    }
    onSave(form);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // --- Group ops ---
  const addGroup = () => setForm((f) => {
    const groups = [...(f.phase_groups || []), {
      name: `Group ${(f.phase_groups || []).length + 1}`,
      order: (f.phase_groups || []).length,
      warehouse_count: null,
      phases: [],
    }];
    return { ...f, phase_groups: reindexGroups(groups) };
  });
  const removeGroup = (gi) => setForm((f) => {
    const groups = (f.phase_groups || []).filter((_, i) => i !== gi);
    return { ...f, phase_groups: reindexGroups(groups) };
  });
  const moveGroup = (gi, dir) => setForm((f) => {
    const groups = [...(f.phase_groups || [])];
    const target = gi + dir;
    if (target < 0 || target >= groups.length) return f;
    [groups[gi], groups[target]] = [groups[target], groups[gi]];
    return { ...f, phase_groups: reindexGroups(groups) };
  });
  const updateGroup = (gi, patch) => setForm((f) => {
    const groups = (f.phase_groups || []).map((g, i) => i === gi ? { ...g, ...patch } : g);
    return { ...f, phase_groups: groups };
  });

  // --- Phase-in-group ops ---
  const addPhase = (gi) => setForm((f) => {
    const groups = (f.phase_groups || []).map((g, i) => {
      if (i !== gi) return g;
      const phases = [...(g.phases || []), {
        id: newPid(),
        name: "New phase",
        duration_weeks: 1,
        order: (g.phases || []).length,
        effort: {},
      }];
      return { ...g, phases };
    });
    return { ...f, phase_groups: groups };
  });
  const removePhase = (gi, pi) => setForm((f) => {
    const groups = (f.phase_groups || []).map((g, i) => {
      if (i !== gi) return g;
      const phases = (g.phases || []).filter((_, j) => j !== pi).map((p, j) => ({ ...p, order: j }));
      return { ...g, phases };
    });
    return { ...f, phase_groups: groups };
  });
  const updatePhase = (gi, pi, patch) => setForm((f) => {
    const groups = (f.phase_groups || []).map((g, i) => {
      if (i !== gi) return g;
      const phases = (g.phases || []).map((p, j) => j === pi ? { ...p, ...patch } : p);
      return { ...g, phases };
    });
    return { ...f, phase_groups: groups };
  });
  const movePhase = (gi, pi, dir) => setForm((f) => {
    const groups = (f.phase_groups || []).map((g, i) => {
      if (i !== gi) return g;
      const phases = [...(g.phases || [])];
      const t = pi + dir;
      if (t < 0 || t >= phases.length) return g;
      [phases[pi], phases[t]] = [phases[t], phases[pi]];
      phases.forEach((p, k) => { p.order = k; });
      return { ...g, phases };
    });
    return { ...f, phase_groups: groups };
  });

  // --- Resource ops ---
  const addResource = () => setForm((f) => {
    const existingNames = new Set((f.resources || []).map((r) => r.name));
    // Ensure uniqueness so the effort-matrix keys don't collide when admin
    // clicks "Add resource" multiple times before renaming.
    let baseName = "New resource";
    let name = baseName;
    let n = 2;
    while (existingNames.has(name)) { name = `${baseName} ${n}`; n += 1; }

    const newResource = {
      name,
      onsite_rate: 100,
      offshore_rate: 60,
      hourly_rate: 100, // legacy mirror
      order: (f.resources || []).length,
    };
    const resources = [...(f.resources || []), newResource];
    // Seed every existing phase's effort matrix with the default weekly hours
    // so the grid shows real, editable values instead of silent zeros.
    const groups = (f.phase_groups || []).map((g) => ({
      ...g,
      phases: (g.phases || []).map((p) => ({
        ...p,
        effort: { ...(p.effort || {}), [name]: DEFAULT_NEW_RESOURCE_HOURS },
      })),
    }));
    return { ...f, resources, phase_groups: groups };
  });
  const removeResource = (idx) => setForm((f) => {
    const name = (f.resources || [])[idx]?.name;
    const resources = (f.resources || []).filter((_, i) => i !== idx).map((r, i) => ({ ...r, order: i }));
    const groups = (f.phase_groups || []).map((g) => ({
      ...g,
      phases: (g.phases || []).map((p) => {
        const effort = { ...(p.effort || {}) };
        if (name && name in effort) delete effort[name];
        return { ...p, effort };
      }),
    }));
    return { ...f, resources, phase_groups: groups };
  });
  const updateResource = (idx, patch) => setForm((f) => {
    const oldName = (f.resources || [])[idx]?.name;
    const resources = (f.resources || []).map((r, i) => i === idx ? { ...r, ...patch } : r);
    let groups = f.phase_groups || [];
    if (patch.name !== undefined && patch.name !== oldName) {
      groups = groups.map((g) => ({
        ...g,
        phases: (g.phases || []).map((p) => {
          const effort = { ...(p.effort || {}) };
          if (oldName && Object.prototype.hasOwnProperty.call(effort, oldName)) {
            effort[patch.name] = effort[oldName];
            delete effort[oldName];
          }
          return { ...p, effort };
        }),
      }));
    }
    return { ...f, resources, phase_groups: groups };
  });
  const moveResource = (idx, dir) => setForm((f) => {
    const resources = [...(f.resources || [])];
    const t = idx + dir;
    if (t < 0 || t >= resources.length) return f;
    [resources[idx], resources[t]] = [resources[t], resources[idx]];
    resources.forEach((r, i) => { r.order = i; });
    return { ...f, resources };
  });

  // Reorder by dragging a resource from one index to another (insert semantics,
  // not swap). Effort matrix is keyed by resource name so row order follows
  // this array automatically — no matrix rewrite required.
  const reorderResource = (from, to) => setForm((f) => {
    const resources = [...(f.resources || [])];
    if (from < 0 || to < 0 || from >= resources.length || to >= resources.length) return f;
    const [moved] = resources.splice(from, 1);
    resources.splice(to, 0, moved);
    resources.forEach((r, i) => { r.order = i; });
    return { ...f, resources };
  });

  // Drag-and-drop state for resources, groups, and phases (namespaced keys).
  const resDnd = useDragReorder();
  const groupDnd = useDragReorder();
  const phaseDnd = useDragReorder();

  // Reorder a top-level phase group via drag-and-drop.
  const reorderGroup = (from, to) => setForm((f) => {
    const groups = [...(f.phase_groups || [])];
    if (from < 0 || to < 0 || from >= groups.length || to >= groups.length) return f;
    const [moved] = groups.splice(from, 1);
    groups.splice(to, 0, moved);
    return { ...f, phase_groups: reindexGroups(groups) };
  });

  // Reorder phases inside the same group (cross-group drag is intentionally
  // not supported — admins should remove/re-add to move between groups).
  const reorderPhase = (gi, from, to) => setForm((f) => {
    const groups = (f.phase_groups || []).map((g, i) => {
      if (i !== gi) return g;
      const phases = [...(g.phases || [])];
      if (from < 0 || to < 0 || from >= phases.length || to >= phases.length) return g;
      const [moved] = phases.splice(from, 1);
      phases.splice(to, 0, moved);
      phases.forEach((p, k) => { p.order = k; });
      return { ...g, phases };
    });
    return { ...f, phase_groups: groups };
  });

  // --- Grid cell updates ---
  const updateCell = (gi, pi, resourceName, raw) => setForm((f) => {
    const groups = (f.phase_groups || []).map((g, i) => {
      if (i !== gi) return g;
      const phases = (g.phases || []).map((p, j) => {
        if (j !== pi) return p;
        const effort = { ...(p.effort || {}) };
        const n = raw === "" ? 0 : Number(raw);
        effort[resourceName] = Number.isFinite(n) ? n : 0;
        return { ...p, effort };
      });
      return { ...g, phases };
    });
    return { ...f, phase_groups: groups };
  });

  const totalDurationWeeks = useMemo(() => {
    return (form.phase_groups || []).reduce(
      (n, g) => n + (g.phases || []).reduce((m, p) => m + (Number(p.duration_weeks) || 0), 0),
      0,
    );
  }, [form.phase_groups]);

  return (
    <div data-testid="impl-edit">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onCancel} className="text-[#025979] hover:text-[#03738C] text-xs inline-flex items-center gap-1" data-testid="impl-back-btn">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to templates
        </button>
      </div>

      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{(form.category || "solution") === "strategy" ? "Strategy" : "Solution"}</p>
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>{form.template_name}</h1>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            {form.solution_label || SOLUTION_LABELS[form.solution_type] || form.solution_type} · Total duration: <strong>{totalDurationWeeks}</strong> weeks
          </p>
        </div>
        <div className="shrink-0 flex gap-2">
          <Button data-testid="impl-save" onClick={handleSave} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
            <Check className="w-3 h-3 mr-1" /> Save
          </Button>
          <Button data-testid="impl-cancel" variant="outline" onClick={onCancel} className="rounded-sm text-xs">
            <X className="w-3 h-3 mr-1" /> Cancel
          </Button>
        </div>
      </div>

      {validationError && (
        <div
          data-testid="impl-validation-error"
          className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-600"
        >
          {validationError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
        <Input
          data-testid="impl-template-name"
          value={form.template_name || ""}
          onChange={(e) => setField("template_name", e.target.value)}
          placeholder="Template name"
          className="rounded-sm text-xs"
        />
        <Input
          data-testid="impl-solution-label"
          value={form.solution_label || ""}
          onChange={(e) => setField("solution_label", e.target.value)}
          placeholder={(form.category || "solution") === "strategy" ? "Strategy label (e.g. Big Bang)" : "Solution label (e.g. SAP EWM Advanced)"}
          className="rounded-sm text-xs"
        />
      </div>

      {/* June 2026 — display-only metadata (does not affect runtime behavior). */}
      <SectionCard
        title="Template Metadata"
        description="Organizational metadata for grouping and display. These fields do not affect implementation generation or runtime behavior."
      >
        <div data-testid="impl-metadata" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <MetaField label="Application">
            <select
              data-testid="impl-meta-application"
              value={form.application || ""}
              onChange={(e) => setField("application", e.target.value)}
              className="w-full rounded-sm text-xs border border-[#E5E7EB] px-2 py-2 bg-white focus:outline-none focus:border-[#025979]"
            >
              <option value="">— Select —</option>
              <option value="SmartEvaluate">SmartEvaluate</option>
              <option value="SmartImprove">SmartImprove</option>
              <option value="SmartBid">SmartBid</option>
              <option value="SmartEWM">SmartEWM</option>
            </select>
          </MetaField>
          <MetaField label="Evaluation Type">
            <select
              data-testid="impl-meta-evaluation-type"
              value={form.evaluation_type || ""}
              onChange={(e) => setField("evaluation_type", e.target.value)}
              className="w-full rounded-sm text-xs border border-[#E5E7EB] px-2 py-2 bg-white focus:outline-none focus:border-[#025979]"
            >
              <option value="">— Not applicable —</option>
              <option value="greenfield_brownfield">Greenfield / Brownfield</option>
              <option value="cloud_migration">Cloud Migration</option>
            </select>
          </MetaField>
          <MetaField label="Category">
            <Input
              data-testid="impl-meta-category"
              value={form.template_category || ""}
              onChange={(e) => setField("template_category", e.target.value)}
              placeholder="e.g. Rollout Strategy, Migration Strategy, Effort Sizing"
              className="rounded-sm text-xs"
            />
          </MetaField>
          <MetaField label="Indicative Duration">
            <Input
              data-testid="impl-meta-duration"
              value={form.indicative_duration || ""}
              onChange={(e) => setField("indicative_duration", e.target.value)}
              placeholder="e.g. 4 Months, 9 Months, Configurable"
              className="rounded-sm text-xs"
            />
          </MetaField>
          <MetaField label="Display Order">
            <Input
              data-testid="impl-meta-display-order"
              type="number"
              min={0}
              step={1}
              value={form.display_order ?? 0}
              onChange={(e) => setField("display_order", Number(e.target.value))}
              className="rounded-sm text-xs"
            />
          </MetaField>
          <MetaField label="Purpose (SmartImprove)">
            <select
              data-testid="impl-meta-purpose"
              value={form.purpose || ""}
              onChange={(e) => setField("purpose", e.target.value)}
              className="w-full rounded-sm text-xs border border-[#E5E7EB] px-2 py-2 bg-white focus:outline-none focus:border-[#025979]"
            >
              <option value="">— Not applicable —</option>
              <option value="implementation_deployment">Implementation / Deployment</option>
              <option value="effort_sizing">Effort Sizing</option>
            </select>
          </MetaField>
          <MetaField label="Default for Application + Purpose">
            <label className="flex items-center gap-2 text-xs text-[#374151] py-2">
              <input
                type="checkbox"
                data-testid="impl-meta-is-default"
                checked={!!form.is_default}
                onChange={(e) => setField("is_default", e.target.checked)}
                className="accent-[#025979]"
              />
              Mark as the active default
            </label>
          </MetaField>
        </div>
      </SectionCard>

      {/* Phase groups — hidden for WRICEF Matrix (flat effort sheet, no rollout). */}
      {!isWricef ? (
        <SectionCard
          title="Phase groups"
          description="Group phases into real-world stages (Foundation, Pilot, Rollout Waves, Hypercare)."
          action={
            <Button data-testid="impl-add-group" onClick={addGroup} variant="outline" className="rounded-sm text-xs">
              <FolderPlus className="w-3 h-3 mr-1" /> Add group
            </Button>
          }
        >
          <div className="space-y-4">
            {(form.phase_groups || []).map((g, gi) => (
              <GroupEditor
                key={gi}
                group={g}
                gi={gi}
                total={(form.phase_groups || []).length}
                onMove={moveGroup}
                onRemove={removeGroup}
                onUpdate={updateGroup}
                onAddPhase={addPhase}
                onRemovePhase={removePhase}
                onUpdatePhase={updatePhase}
                onMovePhase={movePhase}
                groupDnd={groupDnd}
                phaseDnd={phaseDnd}
                onReorderGroup={reorderGroup}
                onReorderPhase={reorderPhase}
              />
            ))}
            {(form.phase_groups || []).length === 0 && (
              <div className="text-xs text-[#9CA3AF] italic">No groups yet. Click "Add group" to start.</div>
            )}
          </div>
        </SectionCard>
      ) : null}

      {/* Resources list */}
      <SectionCard
        title="Resources"
        description="Default project roles with hourly rates."
        action={
          <Button data-testid="impl-add-resource" onClick={addResource} variant="outline" className="rounded-sm text-xs">
            <Plus className="w-3 h-3 mr-1" /> Add resource
          </Button>
        }
      >
        <div className="border border-[#E5E7EB] rounded-sm divide-y divide-[#E5E7EB]">
          {(form.resources || []).map((r, idx) => {
            const key = `res-${idx}`;
            const isDragging = resDnd.isDragging(key);
            const isOver = resDnd.isOver(key);
            const dnd = resDnd.bind(
              key,
              idx,
              (from, to) => reorderResource(from, to),
              (k) => {
                if (k == null) return -1;
                const m = String(k).match(/^res-(\d+)$/);
                return m ? Number(m[1]) : -1;
              },
            );
            return (
            <div
              key={idx}
              data-testid={`impl-res-row-${idx}`}
              {...dnd}
              className={`px-3 py-2 flex flex-wrap items-center gap-2 transition-colors ${
                isDragging ? "opacity-40" : ""
              } ${isOver ? "bg-[#E0F2FE] border-t-2 border-t-[#025979]" : ""}`}
            >
              <span
                data-testid={`impl-res-drag-${idx}`}
                className="cursor-grab active:cursor-grabbing text-[#9CA3AF] hover:text-[#025979] transition-colors"
                title="Drag to reorder"
              >
                <GripVertical className="w-3.5 h-3.5" />
              </span>
              <span className="text-[10px] text-[#9CA3AF] w-5">{idx + 1}</span>
              <ReorderButtons
                testidPrefix={`impl-res-reorder-${idx}`}
                canUp={idx > 0}
                canDown={idx < (form.resources || []).length - 1}
                onUp={() => moveResource(idx, -1)}
                onDown={() => moveResource(idx, +1)}
              />
              <Input
                data-testid={`impl-res-name-${idx}`}
                value={r.name}
                onChange={(e) => updateResource(idx, { name: e.target.value })}
                className="rounded-sm text-xs flex-1 min-w-[160px]"
              />
              <RateInput
                testid={`impl-res-onsite-${idx}`}
                label="Onsite"
                value={r.onsite_rate ?? r.hourly_rate ?? 0}
                onChange={(v) => updateResource(idx, { onsite_rate: v, hourly_rate: v })}
              />
              <RateInput
                testid={`impl-res-offshore-${idx}`}
                label="Offshore"
                value={r.offshore_rate ?? r.hourly_rate ?? 0}
                onChange={(v) => updateResource(idx, { offshore_rate: v })}
              />
              <button
                data-testid={`impl-res-remove-${idx}`}
                onClick={() => removeResource(idx)}
                className="text-[#9CA3AF] hover:text-red-500"
                aria-label="Remove resource"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );})}
          {(form.resources || []).length === 0 && (
            <div className="px-3 py-4 text-center text-[11px] text-[#9CA3AF]">No resources yet.</div>
          )}
        </div>
      </SectionCard>

      {/* Effort matrix —
          • WRICEF Matrix Template: flat Resource × Hours table + totals
          • Other strategy/solution templates: weekly Phase × Resource grid */}
      {isWricef ? (
        <SectionCard
          title="Effort Matrix"
          description="Total hours per resource for the WRICEF object delivery. Rows derive from the Resources section above."
        >
          <WricefEffortMatrix
            resources={form.resources || []}
            effort={form.effort_matrix_simple || {}}
            onChange={(next) => setField("effort_matrix_simple", next)}
          />
        </SectionCard>
      ) : (
        <SectionCard
          title="Weekly effort matrix"
          description="Hours per resource per week for each phase. Group label spans its phase columns."
        >
          <GroupedEffortGrid form={form} onUpdateCell={updateCell} />
        </SectionCard>
      )}
    </div>
  );
}

function normalizeForm(template) {
  const f = JSON.parse(JSON.stringify(template || {}));
  if (!Array.isArray(f.phase_groups)) f.phase_groups = [];
  f.phase_groups = f.phase_groups.map((g, gi) => ({
    name: g.name || `Group ${gi + 1}`,
    order: typeof g.order === "number" ? g.order : gi,
    warehouse_count: g.warehouse_count ?? null,
    phases: (g.phases || []).map((p, pi) => ({
      id: p.id || newPid(),
      name: p.name || "Phase",
      duration_weeks: Number(p.duration_weeks) || 0,
      order: typeof p.order === "number" ? p.order : pi,
      effort: { ...(p.effort || {}) },
    })),
  }));
  if (!Array.isArray(f.resources)) f.resources = [];
  // June 2026 — additive display-only metadata fields.
  f.application = f.application || "";
  f.evaluation_type = f.evaluation_type || "";
  f.template_category = f.template_category || "";
  f.indicative_duration = f.indicative_duration || "";
  f.display_order = Number.isFinite(Number(f.display_order)) ? Number(f.display_order) : 0;
  f.purpose = f.purpose || "";
  f.is_default = !!f.is_default;
  // Feb 2026 — WRICEF Matrix Template carries a flat effort sheet
  // (resource name → total hours). Preserve it on the form so the
  // simplified editor can render and round-trip it.
  if (f.effort_matrix_simple && typeof f.effort_matrix_simple === "object") {
    const cleaned = {};
    Object.keys(f.effort_matrix_simple).forEach((k) => {
      cleaned[k] = Number(f.effort_matrix_simple[k]) || 0;
    });
    f.effort_matrix_simple = cleaned;
  } else {
    f.effort_matrix_simple = {};
  }
  return f;
}

function reindexGroups(groups) {
  return groups.map((g, i) => ({ ...g, order: i }));
}

// ---------------------------------------------------------------------------
// WRICEF Matrix Template — flat effort sheet editor (Resource × Hours).
// Spec (Feb 2026): rows derive from the Resources section; cells edit total
// hours; totals (Functional / Technical / Combined + Cost) auto-recompute.
// ---------------------------------------------------------------------------
function WricefEffortMatrix({ resources, effort, onChange }) {
  const effectiveRate = (r) => {
    const onsite = Number(r.onsite_rate ?? r.hourly_rate ?? 0);
    const offshore = Number(r.offshore_rate ?? r.hourly_rate ?? 0);
    const mode = (r.delivery_model || "offshore").toLowerCase();
    if (mode === "onsite") return onsite;
    if (mode === "blended") return (onsite + offshore) / 2;
    return offshore;
  };

  // Re-sync matrix keys with the live resource list (prune renamed/removed,
  // seed zero for newly added) — keeps the matrix coherent without bouncing
  // through onChange unless the parent state truly differs.
  const liveEffort = { ...(effort || {}) };
  const names = new Set();
  (resources || []).forEach((r) => {
    names.add(r.name);
    if (!(r.name in liveEffort)) liveEffort[r.name] = 0;
  });
  Object.keys(liveEffort).forEach((k) => {
    if (!names.has(k)) delete liveEffort[k];
  });

  const setHours = (resourceName, value) => {
    onChange({ ...liveEffort, [resourceName]: Number(value) || 0 });
  };

  const fnTotal = (resources || [])
    .filter((r) => /functional/i.test(r.name))
    .reduce((a, r) => a + (Number(liveEffort[r.name]) || 0), 0);
  const techTotal = (resources || [])
    .filter((r) => /technical/i.test(r.name))
    .reduce((a, r) => a + (Number(liveEffort[r.name]) || 0), 0);
  const combinedTotal = (resources || [])
    .reduce((a, r) => a + (Number(liveEffort[r.name]) || 0), 0);
  const costTotal = (resources || [])
    .reduce((a, r) => a + (Number(liveEffort[r.name]) || 0) * effectiveRate(r), 0);

  if (!resources?.length) {
    return (
      <div className="text-xs text-[#9CA3AF] italic" data-testid="wricef-matrix-empty">
        Add resources in the section above to populate the effort matrix.
      </div>
    );
  }

  return (
    <div className="border border-[#E5E7EB] rounded-sm overflow-x-auto" data-testid="wricef-effort-matrix">
      <table className="w-full text-xs">
        <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
          <tr>
            <th className="px-3 py-2 font-semibold">Resource</th>
            <th className="px-3 py-2 font-semibold text-right">Effective Rate</th>
            <th className="px-3 py-2 font-semibold text-right">Effort (Hours)</th>
            <th className="px-3 py-2 font-semibold text-right">Cost (USD)</th>
          </tr>
        </thead>
        <tbody>
          {resources.map((r, idx) => {
            const hrs = Number(liveEffort[r.name]) || 0;
            const rate = effectiveRate(r);
            const cost = hrs * rate;
            return (
              <tr key={idx} className="border-t border-[#F1F5F9]">
                <td className="px-3 py-2 text-[#111827] font-semibold">{r.name}</td>
                <td className="px-3 py-2 text-right">
                  <span className="text-[#111827]" data-testid={`wricef-rate-${idx}`}>
                    USD {rate.toLocaleString(undefined, { maximumFractionDigits: 0 })}/hr
                  </span>
                  <div className="text-[10px] text-[#6B7280] mt-0.5">
                    Onsite ${r.onsite_rate ?? r.hourly_rate ?? 0} · Offshore ${r.offshore_rate ?? r.hourly_rate ?? 0}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={hrs}
                    onChange={(e) => setHours(r.name, e.target.value)}
                    className="rounded-sm text-xs h-8 text-right inline-block w-28"
                    data-testid={`wricef-hours-${idx}`}
                  />
                </td>
                <td className="px-3 py-2 text-right font-semibold text-[#111827]" data-testid={`wricef-cost-${idx}`}>
                  USD {cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </td>
              </tr>
            );
          })}
          <tr className="border-t-2 border-[#E5E7EB] bg-[#F8FAFC]">
            <td className="px-3 py-2 text-[11px] font-bold text-[#111827]">Total Combined</td>
            <td className="px-3 py-2 text-right text-[10px] text-[#6B7280]">—</td>
            <td className="px-3 py-2 text-right text-[11px] font-bold text-[#111827]" data-testid="wricef-total-combined">
              {Math.round(combinedTotal).toLocaleString()} hrs
            </td>
            <td className="px-3 py-2 text-right text-[11px] font-bold text-[#111827]" data-testid="wricef-total-cost">
              USD {costTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="px-4 py-3 bg-white border-t border-[#E5E7EB] grid grid-cols-2 md:grid-cols-3 gap-3 text-xs" data-testid="wricef-totals-strip">
        <TotalTile label="Total Functional Hours" value={`${Math.round(fnTotal).toLocaleString()} hrs`} testid="wricef-total-functional" />
        <TotalTile label="Total Technical Hours" value={`${Math.round(techTotal).toLocaleString()} hrs`} testid="wricef-total-technical" />
        <TotalTile label="Total Combined Hours" value={`${Math.round(combinedTotal).toLocaleString()} hrs`} testid="wricef-total-all" />
      </div>
    </div>
  );
}

function TotalTile({ label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF]">{label}</div>
      <div className="text-sm font-bold text-[#111827] mt-0.5">{value}</div>
    </div>
  );
}

function MetaField({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function SectionCard({ title, description, action, children }) {
  return (
    <section className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm mb-5">
      <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo" }}>{title}</h3>
          {description && <p className="text-[11px] text-[#6B7280] mt-0.5">{description}</p>}
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function GroupEditor({
  group, gi, total, onMove, onRemove, onUpdate, onAddPhase, onRemovePhase,
  onUpdatePhase, onMovePhase, groupDnd, phaseDnd, onReorderGroup, onReorderPhase,
}) {
  const isRollout = (group.name || "").toLowerCase().startsWith("rollout wave");
  const gKey = `grp-${gi}`;
  const gDragging = groupDnd?.isDragging?.(gKey);
  const gOver = groupDnd?.isOver?.(gKey);
  const gDnd = groupDnd?.bind?.(
    gKey,
    gi,
    (from, to) => onReorderGroup(from, to),
    (k) => {
      if (k == null) return -1;
      const m = String(k).match(/^grp-(\d+)$/);
      return m ? Number(m[1]) : -1;
    },
  ) || {};

  return (
    <div
      data-testid={`impl-group-${gi}`}
      {...gDnd}
      className={`border rounded-sm bg-[#F8FAFC] p-3 transition-colors ${
        gOver ? "border-[#025979] ring-2 ring-[#025979]/30" : "border-[#025979]/20"
      } ${gDragging ? "opacity-40" : ""}`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span
          data-testid={`impl-group-drag-${gi}`}
          className="cursor-grab active:cursor-grabbing text-[#9CA3AF] hover:text-[#025979] transition-colors"
          title="Drag group to reorder"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </span>
        <ReorderButtons
          testidPrefix={`impl-group-reorder-${gi}`}
          canUp={gi > 0}
          canDown={gi < total - 1}
          onUp={() => onMove(gi, -1)}
          onDown={() => onMove(gi, +1)}
        />
        <Input
          data-testid={`impl-group-name-${gi}`}
          value={group.name}
          onChange={(e) => onUpdate(gi, { name: e.target.value })}
          className="rounded-sm text-xs font-semibold max-w-xs"
          placeholder="Group name (e.g. Foundation, Pilot, Rollout Wave 1)"
        />
        {isRollout && (
          <label className="flex items-center gap-1.5 text-[10px] text-[#6B7280]">
            <span>Warehouses</span>
            <Input
              data-testid={`impl-group-wh-${gi}`}
              type="number"
              min={0}
              step={1}
              value={group.warehouse_count ?? 1}
              onChange={(e) => onUpdate(gi, { warehouse_count: Number(e.target.value) || 0 })}
              className="rounded-sm text-xs w-20"
            />
          </label>
        )}
        <span className="text-[10px] text-[#9CA3AF] ml-auto">{(group.phases || []).length} phase{(group.phases || []).length === 1 ? "" : "s"}</span>
        <Button
          data-testid={`impl-group-add-phase-${gi}`}
          onClick={() => onAddPhase(gi)}
          variant="outline"
          className="rounded-sm text-xs"
        >
          <Plus className="w-3 h-3 mr-1" /> Phase
        </Button>
        <button
          data-testid={`impl-group-remove-${gi}`}
          onClick={() => onRemove(gi)}
          className="text-[#9CA3AF] hover:text-red-500"
          aria-label="Remove group"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="border border-[#E5E7EB] rounded-sm divide-y divide-[#E5E7EB] bg-white">
        {(group.phases || []).map((p, pi) => {
          const pKey = `phase-${gi}-${pi}`;
          const pDragging = phaseDnd?.isDragging?.(pKey);
          const pOver = phaseDnd?.isOver?.(pKey);
          const pDnd = phaseDnd?.bind?.(
            pKey,
            pi,
            (from, to) => onReorderPhase(gi, from, to),
            (k) => {
              if (k == null) return -1;
              const m = String(k).match(/^phase-(\d+)-(\d+)$/);
              // Only accept drops within the same group — cross-group drags no-op.
              if (!m || Number(m[1]) !== gi) return -1;
              return Number(m[2]);
            },
          ) || {};
          // Stop propagation so phase drag events don't bubble up to the
          // enclosing group's drag handlers (which would otherwise start
          // dragging/highlighting the group at the same time).
          const stopWrap = (fn) => (e) => { e.stopPropagation(); return fn?.(e); };
          const wrappedPDnd = Object.fromEntries(
            Object.entries(pDnd).map(([k, v]) => [k, typeof v === "function" ? stopWrap(v) : v]),
          );
          return (
          <div
            key={p.id || pi}
            data-testid={`impl-phase-row-${gi}-${pi}`}
            {...wrappedPDnd}
            className={`px-3 py-2 flex items-center gap-2 transition-colors ${
              pDragging ? "opacity-40" : ""
            } ${pOver ? "bg-[#E0F2FE] border-t-2 border-t-[#025979]" : ""}`}
          >
            <span
              data-testid={`impl-phase-drag-${gi}-${pi}`}
              className="cursor-grab active:cursor-grabbing text-[#9CA3AF] hover:text-[#025979] transition-colors"
              title="Drag phase to reorder"
            >
              <GripVertical className="w-3.5 h-3.5" />
            </span>
            <span className="text-[10px] text-[#9CA3AF] w-5">{pi + 1}</span>
            <ReorderButtons
              testidPrefix={`impl-phase-reorder-${gi}-${pi}`}
              canUp={pi > 0}
              canDown={pi < (group.phases || []).length - 1}
              onUp={() => onMovePhase(gi, pi, -1)}
              onDown={() => onMovePhase(gi, pi, +1)}
            />
            <Input
              data-testid={`impl-phase-name-${gi}-${pi}`}
              value={p.name}
              onChange={(e) => onUpdatePhase(gi, pi, { name: e.target.value })}
              className="rounded-sm text-xs flex-1"
            />
            <Input
              data-testid={`impl-phase-dur-${gi}-${pi}`}
              type="number"
              min={0}
              step={0.5}
              value={p.duration_weeks}
              onChange={(e) => onUpdatePhase(gi, pi, { duration_weeks: Number(e.target.value) })}
              className="rounded-sm text-xs w-24"
            />
            <span className="text-[10px] text-[#9CA3AF] w-10">weeks</span>
            <button
              data-testid={`impl-phase-remove-${gi}-${pi}`}
              onClick={() => onRemovePhase(gi, pi)}
              className="text-[#9CA3AF] hover:text-red-500"
              aria-label="Remove phase"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        );})}
        {(group.phases || []).length === 0 && (
          <div className="px-3 py-3 text-center text-[11px] text-[#9CA3AF]">No phases in this group yet.</div>
        )}
      </div>
    </div>
  );
}

function RateInput({ label, value, onChange, testid }) {
  const n = Number(value);
  const invalid = !Number.isFinite(n) || n < 0;
  return (
    <label className="flex items-center gap-1 text-[10px] text-[#6B7280]">
      <span className="uppercase tracking-wider">{label}</span>
      <span className="text-[#9CA3AF]">$</span>
      <Input
        data-testid={testid}
        type="number"
        min={0}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className={`rounded-sm text-xs w-20 ${invalid ? "border-red-400" : ""}`}
      />
      <span className="text-[#9CA3AF]">/hr</span>
    </label>
  );
}

function ReorderButtons({ canUp, canDown, onUp, onDown, testidPrefix }) {
  const base = "w-5 h-5 flex items-center justify-center rounded-sm border text-[#6B7280] transition-colors";
  const enabled = "border-[#E5E7EB] hover:bg-[#E0F2FE] hover:text-[#025979] hover:border-[#025979]/30";
  const disabled = "border-[#F3F4F6] text-[#E5E7EB] cursor-not-allowed";
  return (
    <div className="inline-flex flex-col gap-0.5" data-testid={testidPrefix}>
      <button type="button" data-testid={`${testidPrefix}-up`} onClick={canUp ? onUp : undefined} disabled={!canUp}
              aria-label="Move up" className={`${base} ${canUp ? enabled : disabled}`}>
        <ChevronUp className="w-3 h-3" />
      </button>
      <button type="button" data-testid={`${testidPrefix}-down`} onClick={canDown ? onDown : undefined} disabled={!canDown}
              aria-label="Move down" className={`${base} ${canDown ? enabled : disabled}`}>
        <ChevronDown className="w-3 h-3" />
      </button>
    </div>
  );
}

function GroupedEffortGrid({ form, onUpdateCell }) {
  const groups = form.phase_groups || [];
  const resources = form.resources || [];
  const flatPhases = [];
  groups.forEach((g, gi) => {
    (g.phases || []).forEach((p, pi) => {
      flatPhases.push({ gi, pi, groupName: g.name, phase: p });
    });
  });

  if (!flatPhases.length || !resources.length) {
    return <p className="text-xs text-[#9CA3AF] italic">Add at least one phase and one resource to edit the matrix.</p>;
  }

  // Group header spans: colspan = phase count per group
  const headerSpans = groups.map((g) => ({
    name: g.name,
    colSpan: (g.phases || []).length,
    warehouseCount: g.warehouse_count,
  })).filter((h) => h.colSpan > 0);

  return (
    <div className="overflow-x-auto" data-testid="impl-effort-grid">
      <table className="min-w-full text-xs border-collapse">
        <thead>
          {/* Group header row */}
          <tr>
            <th className="sticky left-0 bg-white z-10 border-b border-r border-[#E5E7EB]" />
            {headerSpans.map((h, i) => (
              <th
                key={`gh-${i}`}
                colSpan={h.colSpan}
                data-testid={`grid-group-header-${i}`}
                className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#025979] font-semibold bg-[#E0F2FE] border-b-2 border-r border-[#025979]/20"
              >
                {h.name}{h.warehouseCount ? ` · ${h.warehouseCount} wh` : ""}
              </th>
            ))}
          </tr>
          {/* Phase header row */}
          <tr>
            <th className="sticky left-0 bg-white z-10 text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#9CA3AF] border-b border-r border-[#E5E7EB] min-w-[180px]">
              Resource \ Phase
            </th>
            {flatPhases.map(({ phase }, i) => (
              <th
                key={`ph-${i}`}
                className="px-3 py-2 text-left text-[10px] uppercase tracking-wider text-[#9CA3AF] border-b border-[#E5E7EB] min-w-[90px]"
              >
                {phase.name}
                <div className="text-[9px] text-[#9CA3AF] font-normal normal-case tracking-normal">{phase.duration_weeks}w</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {resources.map((r, ri) => (
            <tr key={`r-${ri}`} data-testid={`impl-grid-row-${ri}`}>
              <th className="sticky left-0 bg-white text-left px-3 py-2 font-medium text-[#111827] border-b border-r border-[#E5E7EB]">
                <div>{r.name}</div>
                <div className="text-[10px] text-[#6B7280] font-normal">
                  On ${r.onsite_rate ?? r.hourly_rate ?? 0} · Off ${r.offshore_rate ?? r.hourly_rate ?? 0}
                </div>
              </th>
              {flatPhases.map(({ gi, pi, phase }, ci) => {
                const v = phase.effort?.[r.name] ?? 0;
                return (
                  <td key={`c-${ri}-${ci}`} className="px-1 py-1 border-b border-[#E5E7EB]">
                    <input
                      data-testid={`impl-cell-${ri}-${gi}-${pi}`}
                      type="number"
                      min={0}
                      step={1}
                      value={v}
                      onChange={(e) => onUpdateCell(gi, pi, r.name, e.target.value)}
                      className="w-16 text-right rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs focus:border-[#025979] focus:outline-none"
                    />
                    <span className="text-[9px] text-[#9CA3AF] ml-1">h/wk</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
